import { useEffect, useState } from "react";
import { Badge, Card, CardBody, Col, Row, Spinner } from "reactstrap";
import { FiCalendar, FiMapPin } from "react-icons/fi";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell,
} from "recharts";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import sidebar from "../../utils/sidebar";
import { get } from "../../../../utils/helpers/api";
import { useTranslation } from "react-i18next";
import { useSettings } from "../../../../context/settings";
import StatCard from "../../../../components/Cards/StatCard";
import { IoCallOutline } from "react-icons/io5";
import { MdCrisisAlert, MdOutlineAttachMoney } from "react-icons/md";
import { GiHumanPyramid } from "react-icons/gi";
import { GoTasklist } from "react-icons/go";
import { SiGooglemeet } from "react-icons/si";
import "./styles.css";
import { RiMoneyDollarCircleLine } from "react-icons/ri";

const PRIORITY_COLOR = { high: "danger", normal: "warning", low: "success" };

// Matches the Bootstrap badge colors used by printLeadStatus in ../../utils/helpers.
// "won" gets a distinct teal-green since printLeadStatus reuses "success" for both
// qualified and won, which would make those two indistinguishable in a chart.
const STAGE_COLORS = {
  new: "#0d6efd",
  contacted: "#0dcaf0",
  qualified: "#198754",
  quotation_sent: "#ffc107",
  negotiation: "#6c757d",
  won: "#20c997",
  lost: "#dc3545",
};

const tooltipStyle = { fontSize: "0.78rem", borderRadius: 8, border: "1px solid #eee", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" };

function PipelineByStageChart({ data }) {
  return (
    <PieChart width={120} height={120}>
      <Pie
        data={data}
        dataKey="count"
        nameKey="stage"
        cx={60}
        cy={60}
        innerRadius={30}
        outerRadius={52}
        paddingAngle={2}
      >
        {data.map((entry) => (
          <Cell key={entry.stage} fill={STAGE_COLORS[entry.stage]} />
        ))}
      </Pie>
      <Tooltip formatter={(value, name) => [value, name]} contentStyle={tooltipStyle} />
    </PieChart>
  );
}

function PipelineValueByStageChart({ data }) {
  return (
    <div style={{ flex: 1, minHeight: 220 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 6, right: 8, left: -10, bottom: 24 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 10, fill: "#555" }}
            axisLine={false}
            tickLine={false}
            interval={0}
            angle={-35}
            textAnchor="end"
            height={50}
          />
          <YAxis
            tick={{ fontSize: 11, fill: "#555" }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
            width={40}
          />
          <Tooltip
            formatter={(value) => [value.toLocaleString(), null]}
            labelFormatter={(label, payload) => payload?.[0]?.payload?.label || label}
            contentStyle={tooltipStyle}
            cursor={{ fill: "#f5f3ff" }}
          />
          <Bar dataKey="value" radius={[4, 4, 0, 0]}>
            {data.map((entry) => (
              <Cell key={entry.stage} fill={STAGE_COLORS[entry.stage]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function formatMoney(value, currency = "USD") {
  if (!value && value !== 0) return `0 ${currency}`;
  return `${Number(value).toLocaleString()} ${currency}`;
}

function HomePage() {
  const { t } = useTranslation();
  const { formatDate, formatTime, settings } = useSettings();
  const [loading, setLoading] = useState(true);
  const [openTasks, setOpenTasks] = useState([]);
  const [todayMeetings, setTodayMeetings] = useState([]);
  const [pipelineByStage, setPipelineByStage] = useState([]);
  const [pipelineValueByStage, setPipelineValueByStage] = useState([]);
  const [ kpiData, setKpiData ] = useState({
    total_leads: 0,
    pipeline_value: 0,
    weighted_pipeline: 0,
    total_customers: 0,
    open_tasks: 0,
    calls_today: 0
  });

  const getHomeData = async () => {
    setLoading(true);

    const data = await get('/crm/home');
    if(data.success) {
      setKpiData({
        total_leads: data.total_leads,
        pipeline_value: data.pipeline_value,
        weighted_pipeline: data.weighted_pipeline,
        total_customers: data.total_customers,
        open_tasks: data.open_tasks_count,
        calls_today: data.today_calls_count
      });
      setOpenTasks(data.open_tasks);
      setTodayMeetings(data.today_meetings);
      setPipelineByStage((data.pipeline_by_stage || []).map((s) => ({ ...s, label: t(`crm.${s.stage}`) })));
      setPipelineValueByStage((data.pipeline_value_by_stage || []).map((s) => ({ ...s, label: t(`crm.${s.stage}`) })));

      setLoading(false);
    }
  }

  useEffect(() => {
    getHomeData();
  }, []);

  return(
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader title={t('crm.title')} onReload={getHomeData} />

        {loading ?
          <Spinner color="primary" className="d-block mx-auto my-4" />
        :
          <div className="subheader-page-content scrollable">
            <Row className="g-3 mb-4">
              <Col md="4">
                <StatCard
                  icon={<MdCrisisAlert size={18} color="#6C63FF" />}
                  iconBg="#f0eeff"
                  value={kpiData.total_leads}
                  label={t('crm.totalLeads')}
                />
              </Col>
              <Col md="4">
                <StatCard
                  icon={<MdOutlineAttachMoney size={18} color="#6C63FF" />}
                  iconBg="#f0eeff"
                  value={formatMoney(kpiData.pipeline_value, settings?.sales?.base_currency)}
                  label={t('crm.pipelineValue')}
                />
              </Col>
              <Col md="4">
                <StatCard
                  icon={<RiMoneyDollarCircleLine size={18} color="#6C63FF" />}
                  iconBg="#f0eeff"
                  value={formatMoney(kpiData.weighted_pipeline, settings?.sales?.base_currency)}
                  label={t('crm.weightedPipeline')}
                />
              </Col>
              <Col md="4">
                <StatCard
                  icon={<GiHumanPyramid size={18} color="#6C63FF" />}
                  iconBg="#f0eeff"
                  value={kpiData.total_customers}
                  label={t('crm.totalCustomers')}
                />
              </Col>
              <Col md="4">
                <StatCard
                  icon={<GoTasklist size={18} color="#6C63FF" />}
                  iconBg="#f0eeff"
                  value={kpiData.open_tasks}
                  label={t('crm.openTasks')}
                />
              </Col>
              <Col md="4">
                <StatCard
                  icon={<IoCallOutline size={18} color="#6C63FF" />}
                  iconBg="#f0eeff"
                  value={kpiData.calls_today}
                  label={t('crm.callsToday')}
                />
              </Col>
            </Row>

            <Row className="g-3 mb-4" style={{ alignItems: "stretch" }}>
              <Col lg="6" className="d-flex flex-column">
                <h6 className="fw-bold mb-2">{t('crm.pipelineByStage')}</h6>
                <Card className="flex-grow-1">
                  <CardBody className="d-flex align-items-center gap-4 h-100">
                    {pipelineByStage.every((s) => s.count === 0) ? (
                      <p className="text-center text-muted my-3 w-100">{t('crm.noPipelineData')}</p>
                    ) : (
                      <>
                        <PipelineByStageChart data={pipelineByStage} />
                        <div className="home-donut-legend flex-grow-1">
                          {pipelineByStage.map((s) => {
                            const total = pipelineByStage.reduce((sum, x) => sum + x.count, 0);
                            return (
                              <div key={s.stage} className="home-donut-legend-item">
                                <div className="home-donut-dot" style={{ background: STAGE_COLORS[s.stage] }} />
                                <span className="flex-grow-1">{s.label}</span>
                                <span className="fw-semibold">{s.count}</span>
                                <span className="text-muted ms-2" style={{ fontSize: "0.72rem" }}>
                                  {total > 0 ? Math.round((s.count / total) * 100) : 0}%
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </CardBody>
                </Card>
              </Col>
              <Col lg="6" className="d-flex flex-column">
                <h6 className="fw-bold mb-2">{t('crm.pipelineValueByStage')}</h6>
                <Card className="flex-grow-1">
                  <CardBody className="d-flex flex-column">
                    {pipelineValueByStage.every((s) => s.value === 0) ? (
                      <p className="text-center text-muted my-3">{t('crm.noPipelineData')}</p>
                    ) : (
                      <PipelineValueByStageChart data={pipelineValueByStage} />
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>

            <Row className="g-3">
              <Col lg="6">
                <h6 className="fw-bold mb-2">{t('crm.openTasks')}</h6>
                <Card>
                  <CardBody className="p-0 home-panel-body">
                    {openTasks.length === 0 ? (
                      <p className="text-center text-muted my-3">{t('crm.noOpenTasks')}</p>
                    ) : openTasks.map((task) => (
                      <div key={task.id} className="home-panel-item d-flex align-items-center gap-3 px-3 py-2">
                        <div className="flex-grow-1 overflow-hidden">
                          <div className="home-item-subject fw-semibold text-truncate">{task.subject}</div>
                          <div className="home-item-meta text-muted d-flex align-items-center gap-1 mt-1">
                            <FiCalendar size={11} /> {formatDate(task.due_date)}
                          </div>
                        </div>
                        <Badge color={PRIORITY_COLOR[task.priority] || "secondary"} pill className="home-priority-badge">
                          {task.priority}
                        </Badge>
                      </div>
                    ))}
                  </CardBody>
                </Card>
              </Col>
              <Col lg="6">
                <h6 className="fw-bold mb-2">{t('crm.todaysMeetings')}</h6>
                <Card>
                  <CardBody className="p-0 home-panel-body">
                    {todayMeetings.length === 0 ? (
                      <p className="text-center text-muted my-3">{t('crm.noMeetingsToday')}</p>
                    ) : todayMeetings.map((meeting) => (
                      <div key={meeting.id} className="home-panel-item d-flex align-items-center gap-3 px-3 py-2">
                        <div className="home-meeting-time">
                          <div className="home-meeting-time-from fw-bold">{formatTime(meeting.time_from)}</div>
                          <div className="home-meeting-time-to text-muted">{formatTime(meeting.time_to)}</div>
                        </div>
                        <div className="flex-grow-1 overflow-hidden">
                          <div className="home-item-subject fw-semibold text-truncate">{meeting.title}</div>
                          <div className="home-item-meta text-muted d-flex align-items-center gap-1 mt-1">
                            <FiMapPin size={11} /> {meeting.location}
                          </div>
                        </div>
                      </div>
                    ))}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </div>
        }
      </div>
    </SidebarLayout>
  );
}

export default HomePage;
